import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  RichTextEditorComponent,
  Toolbar,
  Inject,
  Image,
  Link,
  HtmlEditor,
  Count,
  QuickToolbar,
  Table
} from "@syncfusion/ej2-react-richtexteditor";
import i18n from "i18next";
import { L10n } from "@syncfusion/ej2-base";
import useComponentSize from "@rehooks/component-size";

import useSyncfusionSourceCodeToggle from "../../hooks/useSyncfusionSourceCodeToggle";
import { FONT_FAMILY } from "../../constants";
import { FORMAT, TEXT_EDITOR_ES, FONT_SIZES } from "../../constants/richtexteditor";
import { shouldResizeImage, resizeImageToDataUrl, blobToDataUrl } from "../../utils/resize-image";

// RichTextEditor items list
const items = [
  "FontName",
  "Formats",
  "Bold",
  "Italic",
  "Underline",
  "StrikeThrough",
  "FontColor",
  "Alignments",
  "OrderedList",
  "UnorderedList",
  "Outdent",
  "Indent",
  "CreateLink",
  "Image",
  "BackgroundColor",
  "LowerCase",
  "UpperCase",
  "InsertCode",
  "SourceCode",
  "|",
  "FontSize",
  "SuperScript",
  "SubScript",
  "|",
  "CreateTable",
  "|",
  "ClearFormat",
  "Print",
  "FullScreen",
  "|",
  "Undo",
  "Redo"
];

// Module-level so the object keeps its identity across renders (a new literal each render makes the
// Syncfusion wrapper re-apply the image settings on every parent re-render, e.g. typing in Subject)
const insertImageSettings = { saveFormat: "Base64", saveUrl: null };

//RichTextEditor ToolbarSettings
const toolbarSettings = {
  items: items,
  type: "MultiRow",
  enableFloating: false
};

const ComposeMessageEditor = props => {
  const ref = useRef(null);
  const size = useComponentSize(ref);
  const { onChange, defaultValue = "", editorRef, containerPanelRef, attachmentWrapperRef, headerFormRef } = props;
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const [containerPanelHeight, setContainerPanelHeight] = useState(0);
  const [attachmentWrapperHeight, setAttachmentWrapperHeight] = useState(0);
  const [headerFormHeight, setHeaderFormHeight] = useState(0);

  const editorHeight = containerPanelHeight - headerFormHeight - attachmentWrapperHeight - toolbarHeight;

  useSyncfusionSourceCodeToggle('toolsRTE_2');

  useEffect(() => {
    const toolbar = document.getElementById("toolsRTE_2_toolbar");
    if (toolbar) {
      setToolbarHeight(toolbar.offsetHeight);
      toolbar.style.width = `${size.width}px`;
      toolbar.style.position = 'relative';
    }
  }, [size]);

  useEffect(() => {
    L10n.load(TEXT_EDITOR_ES);
    setTimeout(() => {
      const inlineScript = document.createElement('script');
      inlineScript.innerHTML = `var instance7 = window.WEBSPELLCHECKER.init(
        {
          container: document.getElementById("toolsRTE_2_rte-edit-view"),
          theme: "custom",
          serviceId: window.WEBSPELLCHECKER_CODE ,
          autoSearch: true,
          autoDestroy: true,
          autocorrect: false,
          autocomplete: true,
          enforceAI: false,
          lang: "es_ES",
          localization: "es",
          enableBadgeButton: false,
          globalBadge:false,
          enableLanguagesInBadgeButton: false,
          actionItems: [
              "ignoreAll",
              "settings",
              "toggle",
              "proofreadDialog"
          ],
          settingsSections: [
            "languages",
            "general",
            "options"
          ],
        });`;
      document.body.append(inlineScript);
    }, 1000);
  }, []);

  useEffect(() => {
    if (containerPanelRef && containerPanelRef.current) {
      setContainerPanelHeight(containerPanelRef.current.offsetHeight);
    }
    if (attachmentWrapperRef && attachmentWrapperRef.current) {
      setAttachmentWrapperHeight(attachmentWrapperRef.current.offsetHeight);
    }
    if (headerFormRef && headerFormRef.current) {
      setHeaderFormHeight(headerFormRef.current.offsetHeight);
    }
  }, [containerPanelRef, attachmentWrapperRef, headerFormRef]);

  // Heavy images are downscaled off the main thread before being embedded as Base64, instead of letting
  // the editor sanitize, clone (undo stack) and serialize a multi-MB data URL.
  const handleImageSelected = args => {
    const file = args.filesData && args.filesData[0] && args.filesData[0].rawFile;
    const editor = editorRef && editorRef.current;
    if (!editor || !shouldResizeImage(file)) {
      return;
    }
    args.cancel = true;
    editor.imageModule.closeDialog();
    resizeImageToDataUrl(file)
      .then(url => url || blobToDataUrl(file))
      .catch(() => blobToDataUrl(file))
      .then(url => {
        editor.focusIn();
        editor.executeCommand("insertImage", {
          url,
          altText: file.name.replace(/\.[a-zA-Z0-9]+$/, "")
        });
      });
  };

  const calculateHeighOfToolBar = () => {
    const toolBar = document.getElementById('toolsRTE_2_toolbar');
    if (toolBar) {
      return toolBar.offsetHeight - 40 > 12 ? toolBar.offsetHeight - 40 : 12;
    }
    return 12;
  };

  return (
    <Fragment>
      <div className="control-section" id="rte">
        <div ref={ref} className="text-editor-container">
          <RichTextEditorComponent
            id="toolsRTE_2"
            ref={editorRef}
            showCharCount={false}
            locale={i18n.language.startsWith('es') ? 'es' : i18n.language}
            fontFamily={FONT_FAMILY}
            enableTabKey={true}
            insertImageSettings={insertImageSettings}
            toolbarSettings={toolbarSettings}
            imageSelected={handleImageSelected}
            value={defaultValue}
            inline="true"
            blur={content => {
              onChange(content.value);
            }}
            change={content => {
              onChange(content.value);
            }}
            format={FORMAT}
            fontSize={FONT_SIZES}
          >
            <Inject
              services={[
                Toolbar,
                Image,
                Link,
                HtmlEditor,
                Count,
                QuickToolbar,
                Table
              ]}
            />
          </RichTextEditorComponent>
        </div>
      </div>
      <style>{`
        .text-editor-container {
          height: auto;
        }
        .text-editor-container #toolsRTE_2 {
          min-height: 90%;
          max-height: 90%;
        }
        .text-editor-container .e-richtexteditor:not(.e-rte-tb-expand) {
          border: 0px solid rgba(0, 0, 0, 0.12);
        }
        .e-rte-toolbar.e-toolbar {
          height: auto;
          position: fixed;
          width: auto !important;
        }
        .e-content.e-lib.e-keyboard {
          overflow-y: auto;
          height: ${editorHeight}px;
        }
        .e-richtexteditor.e-rte-tb-expand {
          border: none;
        }
        .e-richtexteditor.e-rte-tb-expand .e-rte-content {
          border-bottom: none;
        }
        .e-rte-field .e-checkbox-wrapper {
          display: none;
        }

        #rte .text-editor-container {
          margin: 0 auto;
        }
        
        #rte .e-richtexteditor .e-rte-content .e-content {
          float: left;
          width: 100%;
          max-height: unset !important;
        }
        
        #rte .content-wrapper {
          width: auto;
          margin: 40;
        }
        .wsc-badge {
          top: ${calculateHeighOfToolBar()}px !important;
        }

        input[type='checkbox'] + label:before{
          right: 0;
          left:unset;
        }

        input[type='checkbox']:checked + label:after{
          right: 5px;
          left:unset;
          top:8px;
        }

        .wsc-checkbox+label:after{
          background:#001978;
          right:25px;
          top:8px;
          width:10px;
          height:10px;
        }

        .e-richtexteditor .e-rte-content .e-content h1,
        .e-richtexteditor .e-source-content .e-content h1 {
          font-weight: 700;
        }
        .e-richtexteditor .e-rte-content .e-content h2,
        .e-richtexteditor .e-source-content .e-content h2 {
          font-weight: 700;
        }
        .e-richtexteditor .e-rte-content .e-content h3,
        .e-richtexteditor .e-source-content .e-content h3 {
          font-weight: 700;
        }
        .e-richtexteditor .e-rte-content .e-content h4,
        .e-richtexteditor .e-source-content .e-content h4 {
          font-weight: 700;
        }
        .e-richtexteditor .e-rte-content .e-content h5,
        .e-richtexteditor .e-source-content .e-content h5 {
          font-weight: 700;
        }
        .e-richtexteditor .e-rte-content .e-content h6,
        .e-richtexteditor .e-source-content .e-content h6 {
          font-weight: 700;
        }
        .e-richtexteditor .e-source-content {
          border: none;
          height: 350px;
        }
      `}</style>
    </Fragment>
  );
};

// Parent re-renders on every Redux change (subject, recipients...). Props are stable unless the content changes.
export default React.memo(ComposeMessageEditor);
